import React from "react";
import { BiteDisplayState, TutorSupportLevel } from "../../types/enums";
import type { Range } from "../../types/data";

export const SUPPORT_LEVEL_RANGES: Record<TutorSupportLevel, Range> = {
  [TutorSupportLevel.FLAWLESS]: { min: 0, max: 0 },
  [TutorSupportLevel.LOW]: { min: 1, max: 33 },
  [TutorSupportLevel.MEDIUM]: { min: 33, max: 66 },
  [TutorSupportLevel.HIGH]: { min: 66, max: 100 },
};

export const LAST_LEARNED_KEY = "last_learned" as const;

export type SupportMetadata = {
  key: BiteDisplayState | typeof LAST_LEARNED_KEY;
  label: string;
  info?: React.ReactNode;
};

export const displayStateStyleMap: Record<
  BiteDisplayState,
  {
    caretColor: string;
    backgroundColor: string;
    // Kept apart from backgroundColor so the legend swatch (a static
    // reference, not a live tile) can render the color without the motion.
    animation?: string;
    tagColor: string;
  }
> = {
  [BiteDisplayState.FLAWLESS]: {
    backgroundColor: "bg-[#86AFFF]",
    tagColor: "#416BBF",
    caretColor: "#689AFF",
  },
  [BiteDisplayState.LOW]: {
    backgroundColor: "bg-[#A7D953]",
    tagColor: "#5E8C11",
    caretColor: "#68A600",
  },
  [BiteDisplayState.MEDIUM]: {
    backgroundColor: "bg-[#FFC745]",
    tagColor: "#B87400",
    caretColor: "#FF9D1D",
  },
  [BiteDisplayState.HIGH]: {
    backgroundColor: "bg-[#FF7A7A]",
    tagColor: "#CA0000",
    caretColor: "#F24949",
  },
  [BiteDisplayState.NOT_ANALYZED]: {
    backgroundColor: "bg-green-500",
    tagColor: "#000000",
    caretColor: "#008000",
  },
  [BiteDisplayState.PROCESSING]: {
    backgroundColor: "bg-[#D9D9D9]",
    animation: "motion-safe:animate-pulse",
    tagColor: "#000000",
    caretColor: "#008000",
  },
  [BiteDisplayState.INCOMPLETE]: {
    backgroundColor: "bg-[#FFF3F3] border border-[#FF7A7A]",
    tagColor: "#000000",
    caretColor: "#008000",
  },
  [BiteDisplayState.NOT_STARTED]: {
    backgroundColor: "bg-white border",
    tagColor: "#000000",
    caretColor: "#008000",
  },
};

// Legend square only — no tile is ever painted white. A last-viewed tile keeps
// its own display state and gets a pastel + dashed override (getBiteStyle), so
// this is a stand-in for the legend rather than a real tile appearance.
export const LAST_LEARNED_SWATCH = {
  backgroundColor: "bg-white border border-[#B0B0B0] border-dashed",
  tagColor: "#000000",
  caretColor: "#008000",
};

export const displayStateMetadata: Record<BiteDisplayState, SupportMetadata> = {
  [BiteDisplayState.FLAWLESS]: {
    key: BiteDisplayState.FLAWLESS,
    label: "Flawless",
    info: React.createElement(
      "div",
      null,
      "The student completed this bite ",
      React.createElement(
        "span",
        { className: "text-[#689AFF] font-bold" },
        "flawlessly",
      ),
      " without any tutor support.",
    ),
  },
  [BiteDisplayState.LOW]: {
    key: BiteDisplayState.LOW,
    label: "Low",
    info: React.createElement(
      "div",
      null,
      "The student needed tutor support to answer ",
      React.createElement(
        "span",
        { className: "text-[#68A600] font-bold" },
        `between ${SUPPORT_LEVEL_RANGES[TutorSupportLevel.LOW].min}% and ${SUPPORT_LEVEL_RANGES[TutorSupportLevel.LOW].max}%`,
      ),
      " questions, resulting in EverTutor providing a low level of support.",
    ),
  },
  [BiteDisplayState.MEDIUM]: {
    key: BiteDisplayState.MEDIUM,
    label: "Medium",
    info: React.createElement(
      "div",
      null,
      "The student needed tutor support to answer ",
      React.createElement(
        "span",
        { className: "text-[#FF9D1D] font-bold" },
        `between ${SUPPORT_LEVEL_RANGES[TutorSupportLevel.MEDIUM].min}% and ${SUPPORT_LEVEL_RANGES[TutorSupportLevel.MEDIUM].max}%`,
      ),
      " questions, resulting in EverTutor providing a medium level of support.",
    ),
  },
  [BiteDisplayState.HIGH]: {
    key: BiteDisplayState.HIGH,
    label: "High",
    info: React.createElement(
      "div",
      null,
      "The student needed tutor support to answer ",
      React.createElement(
        "span",
        { className: "text-[#F24949] font-bold" },
        `between ${SUPPORT_LEVEL_RANGES[TutorSupportLevel.HIGH].min}% and ${SUPPORT_LEVEL_RANGES[TutorSupportLevel.HIGH].max}%`,
      ),
      " questions, resulting in EverTutor providing a high level of support.",
    ),
  },
  [BiteDisplayState.NOT_ANALYZED]: {
    key: BiteDisplayState.NOT_ANALYZED,
    label: "Completed, not analyzed",
    info: "Bite was completed, but no support level is available for it",
  },
  [BiteDisplayState.PROCESSING]: {
    key: BiteDisplayState.PROCESSING,
    label: "Processing",
    info: "Analysis is still running for this bite",
  },
  [BiteDisplayState.INCOMPLETE]: {
    key: BiteDisplayState.INCOMPLETE,
    label: "Incomplete",
    info: "Bite was started but not completed",
  },
  [BiteDisplayState.NOT_STARTED]: {
    key: BiteDisplayState.NOT_STARTED,
    label: "Not Started",
    info: "Bite has not been started",
  },
};

export const lastLearnedMetadata: SupportMetadata = {
  key: LAST_LEARNED_KEY,
  label: "Last Learned",
  info: "The last bite the student learned in this lesson",
};

export const supportCutoff = 0.5;
